/**
 * Date Range Utilities for Dashboard Filtering
 *
 * Provides preset definitions, date calculations, and API parameter mapping
 * for filtering dashboard widget data by date ranges.
 */

export interface DatePreset {
  key: string;
  label: string;
  days?: number | null;
  hours?: number;
}

export interface DateRange {
  preset: string;
  startDate: string | null;
  endDate: string | null;
}

export interface DateRangeDates {
  startDate: Date | null;
  endDate: Date | null;
}

export interface ValidationResult {
  valid: boolean;
  error: string | null;
}

/**
 * Standard date presets available in the date range filter
 */
export const DATE_PRESETS: Record<string, DatePreset> = {
  all_time: {
    key: 'all_time',
    label: 'All time',
    days: null // No date filtering
  },
  last_1_hour: {
    key: 'last_1_hour',
    label: 'Last 1 hour',
    hours: 1
  },
  last_6_hours: {
    key: 'last_6_hours',
    label: 'Last 6 hours',
    hours: 6
  },
  last_24_hours: {
    key: 'last_24_hours',
    label: 'Last 24 hours',
    hours: 24
  },
  today: {
    key: 'today',
    label: 'Today',
    days: 1
  },
  last_7_days: {
    key: 'last_7_days',
    label: 'Last 7 days',
    days: 7
  },
  last_30_days: {
    key: 'last_30_days',
    label: 'Last 30 days',
    days: 30
  },
  last_90_days: {
    key: 'last_90_days',
    label: 'Last 90 days',
    days: 90
  },
  this_month: {
    key: 'this_month',
    label: 'This month',
    days: null // Calculated dynamically
  },
  last_month: {
    key: 'last_month',
    label: 'Last month',
    days: null // Calculated dynamically
  },
  this_quarter: {
    key: 'this_quarter',
    label: 'This quarter',
    days: null // Calculated dynamically
  },
  last_quarter: {
    key: 'last_quarter',
    label: 'Last quarter',
    days: null // Calculated dynamically
  },
  this_year: {
    key: 'this_year',
    label: 'This year',
    days: null // Calculated dynamically
  },
  last_year: {
    key: 'last_year',
    label: 'Last year',
    days: null // Calculated dynamically
  },
  custom: {
    key: 'custom',
    label: 'Custom range',
    days: null
  }
};

/**
 * Context-specific preset lists.
 * CloudOps/FinOps: daily granularity — no hourly options.
 * AI Operations / DataOps: sub-daily granularity — include hourly options.
 */
export const CONTEXT_PRESETS: Record<string, string[]> = {
  cloudops: [
    'all_time', 'last_7_days', 'last_30_days', 'last_90_days',
    'this_month', 'last_month', 'this_quarter', 'last_quarter',
    'this_year', 'last_year', 'custom',
  ],
  // AI Ops, DataOps, AgentOps — include hourly + daily
  default: Object.keys(DATE_PRESETS),
};

/**
 * Get presets for a given dashboard context
 */
export const getPresetsForContext = (context?: string): string[] => {
  if (context && CONTEXT_PRESETS[context]) return CONTEXT_PRESETS[context];
  return CONTEXT_PRESETS.default;
};

/**
 * Get the start of day for a given date
 */
const startOfDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Get the end of day for a given date
 */
const endOfDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

/**
 * Convert a preset to actual start and end dates
 */
export const getDateRangeFromPreset = (
  preset: string,
  customStartDate: string | null = null,
  customEndDate: string | null = null
): DateRangeDates => {
  const now = new Date();
  const today = startOfDay(now);
  const endOfToday = endOfDay(now);

  switch (preset) {
    case 'all_time':
      return { startDate: null, endDate: null };

    case 'last_1_hour':
      return {
        startDate: new Date(now.getTime() - 1 * 60 * 60 * 1000),
        endDate: now
      };

    case 'last_6_hours':
      return {
        startDate: new Date(now.getTime() - 6 * 60 * 60 * 1000),
        endDate: now
      };

    case 'last_24_hours':
      return {
        startDate: new Date(now.getTime() - 24 * 60 * 60 * 1000),
        endDate: now
      };

    case 'today':
      return { startDate: today, endDate: endOfToday };

    case 'last_7_days':
      return {
        startDate: new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000),
        endDate: endOfToday
      };

    case 'last_30_days':
      return {
        startDate: new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000),
        endDate: endOfToday
      };

    case 'last_90_days':
      return {
        startDate: new Date(today.getTime() - 89 * 24 * 60 * 60 * 1000),
        endDate: endOfToday
      };

    case 'this_month': {
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return {
        startDate: startOfDay(firstDayOfMonth),
        endDate: endOfToday
      };
    }

    case 'last_month': {
      const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      return {
        startDate: startOfDay(firstDayOfLastMonth),
        endDate: endOfDay(lastDayOfLastMonth)
      };
    }

    case 'this_quarter': {
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const firstMonthOfQuarter = currentQuarter * 3;
      const firstDayOfQuarter = new Date(now.getFullYear(), firstMonthOfQuarter, 1);
      return {
        startDate: startOfDay(firstDayOfQuarter),
        endDate: endOfToday
      };
    }

    case 'last_quarter': {
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
      const yearOfLastQuarter = currentQuarter === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const firstMonthOfLastQuarter = lastQuarter * 3;
      const firstDayOfLastQuarter = new Date(yearOfLastQuarter, firstMonthOfLastQuarter, 1);
      const lastDayOfLastQuarter = new Date(yearOfLastQuarter, firstMonthOfLastQuarter + 3, 0);
      return {
        startDate: startOfDay(firstDayOfLastQuarter),
        endDate: endOfDay(lastDayOfLastQuarter)
      };
    }

    case 'this_year': {
      const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
      return {
        startDate: startOfDay(firstDayOfYear),
        endDate: endOfToday
      };
    }

    case 'last_year': {
      const firstDayOfLastYear = new Date(now.getFullYear() - 1, 0, 1);
      const lastDayOfLastYear = new Date(now.getFullYear() - 1, 11, 31);
      return {
        startDate: startOfDay(firstDayOfLastYear),
        endDate: endOfDay(lastDayOfLastYear)
      };
    }

    case 'custom':
      if (customStartDate && customEndDate) {
        return {
          startDate: startOfDay(new Date(customStartDate)),
          endDate: endOfDay(new Date(customEndDate))
        };
      }
      // Fallback to last 30 days if custom dates not provided
      return {
        startDate: new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000),
        endDate: endOfToday
      };

    default:
      // Default to last 30 days
      return {
        startDate: new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000),
        endDate: endOfToday
      };
  }
};

/**
 * Format a date range for human-readable display
 */
export const formatDateRangeDisplay = (startDate: Date | string, endDate: Date | string): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const formatOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const yearOptions: Intl.DateTimeFormatOptions = { year: 'numeric' };

  const startStr = start.toLocaleDateString('en-US', formatOptions);
  const endStr = end.toLocaleDateString('en-US', formatOptions);
  const yearStr = end.toLocaleDateString('en-US', yearOptions);

  if (start.toDateString() === end.toDateString()) {
    return `${startStr}, ${yearStr}`;
  }

  if (start.getFullYear() === end.getFullYear()) {
    return `${startStr} - ${endStr}, ${yearStr}`;
  }

  const startYearStr = start.toLocaleDateString('en-US', yearOptions);
  return `${startStr}, ${startYearStr} - ${endStr}, ${yearStr}`;
};

/**
 * Get display text for a date range value
 */
export const getDateRangeDisplayText = (dateRange: DateRange | null | undefined): string => {
  if (!dateRange) return DATE_PRESETS.last_30_days.label;

  const preset = DATE_PRESETS[dateRange.preset];

  if (dateRange.preset === 'custom' && dateRange.startDate && dateRange.endDate) {
    return formatDateRangeDisplay(dateRange.startDate, dateRange.endDate);
  }

  return preset?.label || DATE_PRESETS.last_30_days.label;
};

/**
 * Calculate the number of days in a date range
 */
export const getDaysInRange = (startDate: Date | string, endDate: Date | string): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays + 1; // Include both start and end days
};

/**
 * Calculate hours in a date range
 */
export const getHoursInRange = (startDate: Date | string, endDate: Date | string): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60));
};

/**
 * Convert date range to API-specific parameters based on data source
 */
export const dateRangeToAPIParams = (
  dateRange: DateRange | null | undefined,
  dataSource: string
): Record<string, unknown> => {
  if (!dateRange) {
    return getDefaultAPIParams(dataSource);
  }

  const { startDate, endDate } = getDateRangeFromPreset(
    dateRange.preset,
    dateRange.startDate,
    dateRange.endDate
  );

  if (!startDate || !endDate) {
    return {
      days: null,
      hours: null,
      start_date: null,
      end_date: null,
      hoursParam: 17520, // 2 years - backend max for "All Time"
      daysParam: null,
      time_period: 'all_time',
      startDateStr: null,
      endDateStr: null
    };
  }

  const days = getDaysInRange(startDate, endDate);
  const hours = getHoursInRange(startDate, endDate);

  const startDateISO = startDate.toISOString();
  const endDateISO = endDate.toISOString();

  const startDateStr = startDate.toISOString().split('T')[0];
  const endDateStr = endDate.toISOString().split('T')[0];

  switch (dataSource) {
    case 'genaiops':
      return {
        days,
        hours,
        start_date: startDateISO,
        end_date: endDateISO,
        hoursParam: Math.min(hours, 17520) // Cap at 2 years (backend max)
      };

    case 'agentops':
      return {
        days,
        start_date: startDateISO,
        end_date: endDateISO,
        daysParam: days
      };

    case 'platformops':
      return {
        timePeriod: dateRangeToTimePeriod(dateRange.preset),
        startDate: startDateStr,
        endDate: endDateStr,
        customStartDate: startDateStr,
        customEndDate: endDateStr
      };

    case 'hub':
      return {
        days,
        start_date: startDateISO,
        end_date: endDateISO,
        startDate: startDateStr,
        endDate: endDateStr
      };

    default:
      return {
        days,
        start_date: startDateISO,
        end_date: endDateISO
      };
  }
};

/**
 * Get default API parameters for a data source
 */
const getDefaultAPIParams = (dataSource: string): Record<string, unknown> => {
  const defaultDays = 30;
  const defaultHours = defaultDays * 24;

  switch (dataSource) {
    case 'genaiops':
      return { days: defaultDays, hours: defaultHours, hoursParam: 720 };
    case 'agentops':
      return { days: 7, daysParam: 7 };
    case 'platformops':
      return { timePeriod: 'current_month' };
    default:
      return { days: defaultDays };
  }
};

/**
 * Convert preset to PlatformOps timePeriod string
 */
const dateRangeToTimePeriod = (preset: string): string => {
  const mapping: Record<string, string> = {
    all_time: 'all_time',
    today: 'current_month',
    last_7_days: 'current_month',
    last_30_days: 'last_month',
    last_90_days: 'last_3_months',
    this_month: 'current_month',
    last_month: 'last_month',
    this_quarter: 'custom',
    last_quarter: 'custom',
    this_year: 'custom',
    last_year: 'custom',
    custom: 'custom'
  };
  return mapping[preset] || 'current_month';
};

/**
 * Generate a cache key string from date range
 */
export const dateRangeToCacheKey = (dateRange: DateRange | null | undefined): string => {
  if (!dateRange) return 'default';

  if (dateRange.preset === 'custom' && dateRange.startDate && dateRange.endDate) {
    return `custom-${dateRange.startDate}-${dateRange.endDate}`;
  }

  return dateRange.preset || 'default';
};

/**
 * Validate that a date range is valid
 */
export const validateDateRange = (dateRange: DateRange | null | undefined): ValidationResult => {
  if (!dateRange) {
    return { valid: true, error: null };
  }

  if (dateRange.preset === 'custom') {
    if (!dateRange.startDate || !dateRange.endDate) {
      return { valid: false, error: 'Start and end dates are required for custom range' };
    }

    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return { valid: false, error: 'Invalid date format' };
    }

    if (start > end) {
      return { valid: false, error: 'Start date must be before end date' };
    }

    const days = getDaysInRange(start, end);
    if (days > 365) {
      return { valid: false, error: 'Date range cannot exceed 1 year' };
    }
  }

  return { valid: true, error: null };
};

/**
 * Create a default date range object
 */
export const createDefaultDateRange = (preset = 'last_30_days'): DateRange => {
  return {
    preset,
    startDate: null,
    endDate: null
  };
};
